import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RESET } from "@/constants/testIds/app";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get("token"), [location.search]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError("Token mancante. Richiedi un nuovo link di reset.");
  }, [token]);

  const formatError = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return "Impossibile reimpostare la password.";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return;
    if (password.length < 12) {
      setError("La password deve avere almeno 12 caratteri");
      return;
    }
    if (password !== confirm) {
      setError("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/", { replace: true }), 2500);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[var(--bg-1)] p-6"
      data-testid={RESET.container}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="font-serif text-3xl tracking-tight leading-none text-[var(--fg-1)]">
            Recen<span className="italic text-[#00F2F4]">Up</span>
          </div>
        </div>

        <div className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl p-8 shadow-[0_2px_12px_rgba(45,41,38,0.04)]">
          {success ? (
            <div className="text-center">
              <div
                data-testid={RESET.successMessage}
                className="inline-flex items-center gap-2 text-[#4A8A6E] font-sans text-sm font-semibold mb-3"
              >
                <CheckCircle2 size={18} /> Password aggiornata
              </div>
              <p className="text-sm text-[var(--fg-3)]">
                Verrai reindirizzato alla pagina di accesso...
              </p>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F2F4] font-sans font-semibold">
                Recupero password
              </div>
              <h1 className="mt-2 font-serif text-4xl text-[var(--fg-1)] tracking-tight leading-tight">
                Imposta una <span className="italic">nuova</span> password
              </h1>
              <p className="mt-3 text-sm text-[var(--fg-3)]">
                Scegli una password sicura di almeno 12 caratteri, evitando quelle troppo comuni.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    Nuova password
                  </Label>
                  <div className="mt-2 relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                    <Input
                      data-testid={RESET.passwordInput}
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Almeno 12 caratteri"
                      className="pl-9"
                      disabled={!token}
                    />
                  </div>
                  <PasswordStrengthMeter password={password} testId="reset-password-meter" />
                </div>

                <div>
                  <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    Conferma password
                  </Label>
                  <div className="mt-2 relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Ripeti la password"
                      className="pl-9"
                      disabled={!token}
                    />
                  </div>
                </div>

                {error && (
                  <div
                    data-testid={RESET.errorMessage}
                    className="text-xs font-sans text-[#C6423B] bg-[#C6423B]/10 border border-[#C6423B]/20 rounded-lg px-3 py-2"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid={RESET.submitBtn}
                  disabled={loading || !token}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] rounded-full py-3.5 px-6 font-sans text-sm font-semibold disabled:opacity-60 focus:ring-2 focus:ring-[#00F2F4]/50 focus:outline-none"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  {loading ? "Aggiornamento..." : "Aggiorna password"}
                  {!loading && <ArrowRight size={15} />}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/"
            data-testid={RESET.backToLogin}
            className="text-xs font-sans text-[var(--fg-3)] hover:text-[var(--fg-1)]"
            style={{ transition: "color 0.2s ease" }}
          >
            ← Torna al login
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
